import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LoadingState, StatePanel, V2Tabs } from '@sakhtar/design-system/react';
import { useCapabilities } from '../../lib/capabilities.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { CommunicationWorkspacePage } from './CommunicationWorkspacePage.js';
import { SmsRoutinesTab } from './sms/SmsRoutinesTab.js';
import { SmsTemplateEditor } from './sms/SmsTemplateEditor.js';
import { SmsTemplatesTab } from './sms/SmsTemplatesTab.js';
import type { SmsRoutinesResponse, SmsTemplatesResponse } from './sms/smsTypes.ts';
import './sms/smsPanel.css';

const TABS = [
  { id: 'routines', label: 'روال‌های خودکار' },
  { id: 'templates', label: 'قالب‌ها' },
  { id: 'history', label: 'تاریخچه مشتری' },
] as const;
type TabId = (typeof TABS)[number]['id'];

function SmsMark() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 5.5h16v11h-9.5L6 20v-3.5H4z" />
      <path d="M8 10h8M8 13h5" />
    </svg>
  );
}

/**
 * «پنل پیامک»: manage routine SMS (templates + four routines) and read a customer's SMS history.
 * Configuration only — sending is not connected, and the UI says so.
 */
export function SmsPanelPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [params, setParams] = useSearchParams();
  const { hasCapability } = useCapabilities();
  const [creating, setCreating] = useState<number | null>(null);

  const canReadTemplates = hasCapability('crm.sms-template.read');
  const canReadRoutines = hasCapability('crm.sms-routine.read');
  const requested = params.get('tab');
  const tab: TabId = TABS.some((t) => t.id === requested) ? (requested as TabId) : 'routines';

  const templates = useApiGet<SmsTemplatesResponse>(
    slug && canReadTemplates ? `${base}/api/v1/crm/sms/templates` : null,
    [slug, canReadTemplates],
  );
  const routines = useApiGet<SmsRoutinesResponse>(
    slug && canReadRoutines ? `${base}/api/v1/crm/sms/routines` : null,
    [slug, canReadRoutines],
  );

  if (!slug) return null;

  const reload = () => {
    templates.reload();
    routines.reload();
  };
  const setTab = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  };

  const denied = (title: string) => <StatePanel variant="permission_denied" title={title} />;
  const failed = (status: number) => (
    <StatePanel
      variant={status === 403 ? 'permission_denied' : 'network_error'}
      title={status === 403 ? 'دسترسی شما برای این بخش کافی نیست' : 'اطلاعات پیامک در دسترس نیست'}
    />
  );

  let body;
  if (tab === 'history') {
    body = <CommunicationWorkspacePage channel="sms" icon="sms" title="" subtitle="" embedded />;
  } else if (tab === 'routines') {
    body = !canReadRoutines ? (
      denied('برای مشاهدهٔ روال‌های پیامک دسترسی ندارید')
    ) : routines.status === 'loading' ? (
      <LoadingState />
    ) : routines.status === 'error' ? (
      failed(routines.error.status)
    ) : (
      <SmsRoutinesTab
        base={base}
        routines={routines.data}
        templates={templates.status === 'ready' ? templates.data : null}
        canManage={hasCapability('crm.sms-routine.manage')}
        onChanged={reload}
        onCreateTemplate={() => setCreating(Date.now())}
      />
    );
  } else {
    body = !canReadTemplates ? (
      denied('برای مشاهدهٔ قالب‌های پیامک دسترسی ندارید')
    ) : templates.status === 'loading' ? (
      <LoadingState />
    ) : templates.status === 'error' ? (
      failed(templates.error.status)
    ) : (
      <SmsTemplatesTab
        base={base}
        data={templates.data}
        routineSpecs={routines.status === 'ready' ? routines.data.catalog.routines : []}
        canManage={hasCapability('crm.sms-template.manage')}
        onChanged={reload}
      />
    );
  }

  return (
    <div className="sms-panel">
      <header className="sms-panel__head">
        <span className="sms-panel__icon">
          <SmsMark />
        </span>
        <div>
          <h1>پنل پیامک</h1>
          <p>قالب‌ها و روال‌های پیامک را تنظیم کنید و تاریخچهٔ هر مشتری را ببینید.</p>
        </div>
      </header>
      <V2Tabs
        items={TABS.map((t) => ({ id: t.id, label: t.label }))}
        activeId={tab}
        onChange={setTab}
      />
      {body}

      {creating !== null && templates.status === 'ready' ? (
        <SmsTemplateEditor
          key={creating}
          base={base}
          open
          template={null}
          starter={null}
          variables={templates.data.variables}
          onClose={() => setCreating(null)}
          onSaved={() => {
            setCreating(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
